import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useElementWidth } from '../../hooks/useElementWidth'
import type { DealerDashboardDay } from '../../types'

const HEIGHT = 220
const PAD = { top: 12, right: 8, bottom: 28, left: 36 }

// цвета серий: осмотры — светлый тон бренда, отправлено — navy, просмотрено — зелёный
export const SERIES = [
  { key: 'inspections', color: '#A9BCE8', width: 0.8 },
  { key: 'sent', color: '#203E78', width: 0.52 },
  { key: 'viewed', color: '#12B76A', width: 0.26 },
] as const

// «красивый» верх шкалы: 1, 2, 5 × 10^n
function niceMax(value: number): number {
  if (value <= 4) return 4
  const pow = Math.pow(10, Math.floor(Math.log10(value)))
  const n = value / pow
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return nice * pow
}

// "YYYY-MM-DD" → локальная дата без сдвига по UTC
function parseDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d)
}

// Столбцы по дням: три серии наложены друг на друга (уже → уже), чтобы 90 дней оставались читаемыми
export default function DayChart({ days }: { days: DealerDashboardDay[] }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)

  const max = niceMax(Math.max(0, ...days.flatMap((d) => [d.inspections, d.sent, d.viewed])))
  const plotW = Math.max(0, width - PAD.left - PAD.right)
  const plotH = HEIGHT - PAD.top - PAD.bottom
  const groupW = days.length ? plotW / days.length : 0
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f))
  // подписи по оси X: не больше ~8, чтобы не слипались
  const labelEvery = Math.max(1, Math.ceil(days.length / Math.max(1, Math.floor(plotW / 72))))
  const fmtDay = (day: string) => parseDay(day).toLocaleDateString(lang, { day: 'numeric', month: 'short' })

  const hovered = hover !== null ? days[hover] : null
  const tooltipLeft = hover !== null ? Math.min(Math.max(PAD.left + groupW * (hover + 0.5), 80), width - 80) : 0

  return (
    <div className="dealer-chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label={t('dealer.dashboard.chart_title')} onMouseLeave={() => setHover(null)}>
          {ticks.map((tick) => (
            <g key={tick}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} stroke="#E2E6EF" strokeDasharray={tick === 0 ? undefined : '3 3'} />
              <text x={PAD.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" fontSize="11" fill="#5B6578">{tick}</text>
            </g>
          ))}

          {days.map((d, i) => {
            const cx = PAD.left + groupW * (i + 0.5)
            return (
              <g key={d.date}>
                {hover === i && <rect x={cx - groupW / 2} y={PAD.top} width={groupW} height={plotH} fill="#F1F5FF" />}
                {SERIES.map((s) => {
                  const v = d[s.key]
                  if (!v) return null
                  const w = Math.max(1.5, groupW * s.width)
                  return <rect key={s.key} x={cx - w / 2} y={y(v)} width={w} height={y(0) - y(v)} fill={s.color} rx={Math.min(2, w / 4)} />
                })}
                {i % labelEvery === 0 && (
                  <text x={cx} y={HEIGHT - 8} textAnchor="middle" fontSize="11" fill="#5B6578">{fmtDay(d.date)}</text>
                )}
                {/* прозрачная зона наведения на весь день */}
                <rect
                  x={cx - groupW / 2}
                  y={PAD.top}
                  width={groupW}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                  onTouchStart={() => setHover(i)}
                />
              </g>
            )
          })}
        </svg>
      )}

      {hovered && (
        <div className="dealer-chart__tooltip" style={{ left: tooltipLeft }}>
          <strong>{parseDay(hovered.date).toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' })}</strong>
          {SERIES.map((s) => (
            <span key={s.key} className="dealer-chart__tooltip-row">
              <span className="dealer-chart__swatch" style={{ background: s.color }} />
              {t(`dealer.dashboard.series_${s.key}`)}
              <b>{hovered[s.key]}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
